import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';

/**
 * User profile page
 * Displays user information, direct manager, manager chain, etc.
 */
const UserProfile = () => {
    const { user } = useAuth();
    const { t } = useI18n(['common', 'profile']);
    const [activeTab, setActiveTab] = useState('info');

    const managerChain = user?.managerChain || [];
    const directManager = user?.directManager || {};

    const InfoField = ({ label, value, icon }) => (
        <div className="flex items-start py-3 border-b border-gray-100 last:border-0">
            <div className="w-8 text-gray-400">{icon}</div>
            <div className="flex-1">
                <p className="text-sm text-gray-500">{label}</p>
                <p className="text-gray-900 font-medium">{value || '-'}</p>
            </div>
        </div>
    );

    const ManagerCard = ({ manager, level }) => (
        <div className={`relative flex items-center p-4 rounded-lg border border-gray-200 bg-white`}>
            {level && (
                <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">
                    {level}
                </div>
            )}
            <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-xl font-bold text-white mr-4">
                {manager.displayName?.charAt(0) || '?'}
            </div>
            <div className="flex-1">
                <p className="font-semibold text-gray-900">{manager.displayName}</p>
                <p className="text-sm text-gray-600">{manager.jobTitle}</p>
                <p className="text-xs text-gray-500">{manager.department}</p>
            </div>
            <div className="text-right text-xs text-gray-500">
                <p>{manager.email}</p>
                {manager.officeLocation && <p>{manager.officeLocation}</p>}
            </div>
        </div>
    );

    return (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
            {/* Page title */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">{t('profile:title')}</h1>
                <p className="text-gray-600 mt-1">{t('profile:description')}</p>
            </div>

            {/* User avatar and basic information */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex items-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-3xl font-bold text-white">
                        {user?.displayName?.charAt(0) || user?.givenName?.charAt(0) || '?'}
                    </div>
                    {/* two column of info */}
                    <div className="ml-6 flex-1">
                        <h2 className="text-xl font-semibold text-gray-900">{user?.displayName || 'N/A'}</h2>
                        <p className="text-gray-600">{user?.jobTitle || 'N/A'}</p>
                        <p className="text-gray-500">{user?.department || 'N/A'}</p>
                    </div>
                    <div className='text-right text-gray-500'>
                        <p>{user.email}</p>
                        {user.officeLocation && <p>{user.officeLocation}</p>}
                    </div>
                </div>
            </div>

            {/* Tab options */}
            <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1">
                <button
                    onClick={() => setActiveTab('info')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'info'
                            ? 'bg-white text-gray-900 shadow'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    📋 {t('profile:basicInfo')}
                </button>
                <button
                    onClick={() => setActiveTab('manager')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'manager'
                            ? 'bg-white text-gray-900 shadow'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    👔 {t('profile:managerInfo')}
                </button>
                <button
                    onClick={() => setActiveTab('chain')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'chain'
                            ? 'bg-white text-gray-900 shadow'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    🔗 {t('profile:managerChain')}
                </button>
            </div>

            {/* Basic Information */}
            {activeTab === 'info' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile:accountInfo')}</h3>
                    <InfoField label={t('profile:displayName')} value={user?.displayName} icon="👤" />
                    <InfoField label={t('profile:email')} value={user?.email} icon="📧" />
                    <InfoField label={t('profile:username')} value={user?.username} icon="🔑" />
                    <InfoField label={t('profile:employeeId')} value={user?.employeeId} icon="🏷️" />
                    <InfoField label={t('profile:jobTitle')} value={user?.jobTitle} icon="💼" />
                    <InfoField label={t('profile:department')} value={user?.department} icon="🏢" />
                    <InfoField label={t('profile:officeLocation')} value={user?.officeLocation} icon="📍" />
                </div>
            )}

            {/* Manager Information */}
            {activeTab === 'manager' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile:directManager')}</h3>
                    {directManager ? (
                        <ManagerCard manager={directManager} />
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <p className="text-4xl mb-2">👤</p>
                            <p>No Direct Manager Set</p>
                        </div>
                    )}
                </div>
            )}

            {/* Manager Chain */}
            {activeTab === 'chain' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">{t('profile:managerChain')}</h3>
                    </div>

                    {managerChain.length > 0 ? (
                        <div className="space-y-4 ml-4">
                            {managerChain.map((manager, index) => (
                                <div key={manager.userId || index} className="relative">
                                    {index < managerChain.length - 1 && (
                                        <div className="absolute left-3 top-16 w-0.5 h-8 bg-gray-300"></div>
                                    )}
                                    <ManagerCard manager={manager} level={manager.level || index + 1} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <p className="text-4xl mb-2">🔗</p>
                            <p>{t('profile:noManagerChain')}</p>
                            <p className="text-sm mt-2">{t('profile:managerChainNote')}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserProfile;
